import React, { useEffect, useState } from 'react';
import { CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ReportLayout, exportToCsv, useReportDates } from './ReportLayout';
import { reportsApi } from '../../api/reports';

const columns: GridColDef[] = [
  { field: 'material_code', headerName: 'Material Code', flex: 1, minWidth: 130 },
  { field: 'material_type', headerName: 'Type', width: 100 },
  { field: 'material_quality', headerName: 'Quality', width: 100 },
  { field: 'uom', headerName: 'UOM', width: 70 },
  { field: 'rate', headerName: 'Rate', type: 'number', width: 80 },
  { field: 'opening_stock', headerName: 'Opening', type: 'number', width: 100 },
  { field: 'received', headerName: 'Received', type: 'number', width: 100 },
  { field: 'consumed', headerName: 'Consumed', type: 'number', width: 100 },
  { field: 'adjusted', headerName: 'Adjusted', type: 'number', width: 100 },
  { field: 'closing_stock', headerName: 'Closing', type: 'number', width: 100 },
  { field: 'wastage_pct', headerName: 'Wastage %', type: 'number', width: 100 },
];

export default function InventoryConsumptionReport() {
  const { startDate, endDate, setStartDate, setEndDate } = useReportDates();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [materialType, setMaterialType] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportsApi.getInventoryConsumption({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        material_type: materialType || undefined,
      });
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, materialType]);

  return (
    <ReportLayout
      title="Inventory / Material Consumption Report"
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      onExport={() => exportToCsv('inventory-consumption.csv', data)}
      loading={loading}
      extraFilters={
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Material Type</InputLabel>
          <Select value={materialType} label="Material Type" onChange={(e) => setMaterialType(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="FABRIC">Fabric</MenuItem>
            <MenuItem value="LACE">Lace</MenuItem>
            <MenuItem value="BUTTON">Button</MenuItem>
            <MenuItem value="SEMI_FINISHED">Semi Finished</MenuItem>
          </Select>
        </FormControl>
      }
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row.material_id}
          autoHeight
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
        />
      )}
    </ReportLayout>
  );
}
